import React, { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import "./StudentPortal.css";
import StudentAdminSidebar from "./StudentAdminSidebar";
import { logoutToLogin } from "../utils/sessionLogout";
import { getUserRole } from "../utils/userRoles";

function SidebarToggleIcon({ open }) {
  return (
    <svg
      className={`sidebar-toggle-svg ${open ? "is-open" : "is-closed"}`}
      width="28"
      height="28"
      viewBox="0 0 28 28"
      aria-hidden="true"
      focusable="false"
    >
      <line x1="6" y1="8" x2="22" y2="8" />
      <line x1="6" y1="14" x2="22" y2="14" />
      <line x1="6" y1="20" x2="22" y2="20" />
    </svg>
  );
}

export default function TutorWorkspaceShell() {
  const navigate = useNavigate();
  const location = useLocation();
  const studentId = String(localStorage.getItem("student_id") || "").toUpperCase();
  const studentName = localStorage.getItem("student_name") || studentId;
  const auth_role = getUserRole(
    {
      student_id: studentId,
      role: localStorage.getItem("role"),
      is_admin: localStorage.getItem("is_admin") === "1",
    },
    localStorage.getItem("role")
  );
  const roleLabel = auth_role ? auth_role.charAt(0).toUpperCase() + auth_role.slice(1) : "Tutor";
  const [sidebarOpen, setSidebarOpen] = useState(() =>
    typeof window === "undefined" ? true : window.innerWidth > 860
  );

  useEffect(() => {
    const onResize = () => {
      setSidebarOpen(window.innerWidth > 860);
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape" && window.innerWidth <= 860) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener("resize", onResize);
    window.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, []);

  const currentPath = location.pathname || "";
  const active_dashboard = useMemo(() => {
    if (currentPath.startsWith("/tutor")) return "tutor_dashboard";
    return "unknown";
  }, [currentPath]);
  const display_context = useMemo(() => {
    if (auth_role === "admin" && active_dashboard === "tutor_dashboard") {
      return "Tutor Dashboard (Admin View)";
    }
    return "Tutor Portal";
  }, [auth_role, active_dashboard]);
  const sidebarBranding = auth_role === "admin" ? "Administrative Access" : "Creative English Curriculum";
  const isAdmin = auth_role === "admin";
  const activePath = useMemo(() => {
    if (currentPath.startsWith("/tutor/review")) return "needs-review";
    if (currentPath.startsWith("/tutor/needs-review")) return "needs-review";
    if (currentPath.startsWith("/tutor/student-progress")) return "student-progress";
    if (currentPath.startsWith("/tutor/curriculum")) return "curriculum";
    if (currentPath.startsWith("/tutor/notifications")) return "notifications";
    if (currentPath.startsWith("/tutor/account")) return "account";
    if (currentPath.startsWith("/tutor/student/")) return "student-progress";
    return "needs-review";
  }, [currentPath]);

  const closeMobileDrawer = () => {
    if (window.innerWidth <= 860) {
      setSidebarOpen(false);
    }
  };

  const goTo = (path) => {
    navigate(path);
    closeMobileDrawer();
  };

  return (
    <div className={`sp-shell ${sidebarOpen ? "sp-shell--sidebar-open" : "sp-shell--sidebar-closed"} sp-shell--curriculum`}>
      {sidebarOpen && window.innerWidth <= 860 ? (
        <button
          type="button"
          className="sp-sidebar-backdrop"
          aria-label="Close tutor navigation"
          style={{ pointerEvents: "auto" }}
          onClick={() => setSidebarOpen(false)}
        />
      ) : null}

      <header className="sp-topbar" aria-hidden="false">
        <button
          type="button"
          className="sp-menu-toggle"
          aria-label={sidebarOpen ? "Collapse navigation" : "Expand navigation"}
          aria-expanded={sidebarOpen ? "true" : "false"}
          onClick={() => setSidebarOpen((value) => !value)}
        >
          <SidebarToggleIcon open={sidebarOpen} />
        </button>

        <div className="sp-search" role="banner" aria-label={display_context}>
          <div style={{ fontWeight: 900, letterSpacing: ".02em" }}>Writing Studio</div>
          <div style={{ fontSize: 12, opacity: 0.78, marginTop: 2 }}>{display_context}</div>
        </div>

        <div className="sp-user-actions">
          <div className="sp-user">
            <div className="sp-avatar" aria-hidden>
              <span className="sp-avatar-letter">
                {String(studentName || studentId).replace(/\s+/g, "").slice(0, 2).toUpperCase() || "TU"}
              </span>
            </div>
            <div className="sp-user-meta">
              <div className="sp-name">{studentName || studentId}</div>
              <div className="sp-user-sub">{roleLabel}</div>
            </div>
          </div>
        </div>
      </header>

      <div className="sp-body">
        <aside className="sp-side" aria-label="Tutor navigation">
          {isAdmin ? (
            <StudentAdminSidebar
              studentId={studentId}
              currentRoute={currentPath}
              navigate={navigate}
              closeMobileDrawer={closeMobileDrawer}
              showStudentProgressLink
              showTutorDashboardLink
              showDevResetTools
              llmUsage={{ session_total_requests: 0 }}
            />
          ) : (
            <>
              <div className="sp-side-top">
                <div className="sp-side-brand">
                  <img
                    src={process.env.PUBLIC_URL + "/hirilogo.png"}
                    alt="HIRI logo"
                    className="sp-side-logo"
                  />
                  <div className="sp-side-text">
                    <div className="sp-side-title">Writing Studio</div>
                    <div className="sp-side-sub">{sidebarBranding}</div>
                  </div>
                </div>

                <nav className="sp-side-nav" data-tour="dashboard-sidepanel">
                  <button
                    className={`sp-navitem ${activePath === "needs-review" ? "active" : ""}`}
                    type="button"
                    onClick={() => goTo("/tutor/needs-review")}
                  >
                    Needs Review
                  </button>
                  <button
                    className={`sp-navitem ${activePath === "student-progress" ? "active" : ""}`}
                    type="button"
                    onClick={() => goTo("/tutor/student-progress")}
                  >
                    Student Progress
                  </button>
                  <button
                    className={`sp-navitem ${activePath === "curriculum" ? "active" : ""}`}
                    type="button"
                    onClick={() => goTo("/tutor/curriculum")}
                  >
                    Curriculum
                  </button>
                  <button
                    className={`sp-navitem ${activePath === "notifications" ? "active" : ""}`}
                    type="button"
                    onClick={() => goTo("/tutor/notifications")}
                  >
                    Notifications
                  </button>
                  <button
                    className={`sp-navitem ${activePath === "account" ? "active" : ""}`}
                    type="button"
                    onClick={() => goTo("/tutor/account")}
                  >
                    Account
                  </button>
                </nav>
              </div>

              <div className="sp-side-bottom">
                <div className="sp-id-chip">ID: {studentId}</div>
                <button className="sp-link" type="button" onClick={() => logoutToLogin(navigate)}>
                  Log out
                </button>
              </div>
            </>
          )}
        </aside>

        <main className="sp-main">
          <div className="sp-main-scroll">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
